import type { NovelItem_Status } from '@app/_proto/Protos/novels';
import type { PlacementEntryItem } from '@app/_proto/Protos/placements';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import { parseNovelStatus } from '@app/domains/common/utils';
import { getNovelPath } from '@app/domains/common/utils/path';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import { isOneOfKind } from '@app/libs/grpc';
import clsx from 'clsx';
import { type ReactNode, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';

import type { SeriesTypeItem } from '@app/_proto/Protos/series_types';
import { logSelectSeries } from '@app/analytics';
import BackgroundLayer from './BackgroundLayer';
import CharacterImageLayer from './CharacterImageLayer';
import ContentLayer from './ContentLayer';
import GradationLayer from './GradationLayer';

type HomeFeaturedCardProps = {
    content?: ParsedPlacementParams;
};

export default function HomeFeaturedCard({ content }: HomeFeaturedCardProps) {
    if (!content) return null;
    return (
        <HomeFeaturedCardContext.Provider value={content}>
            <FeaturedCard>
                <div className="absolute top-0 z-[1] h-full w-full overflow-hidden rounded-12">
                    <FeaturedCard.BackgroundLayer />
                </div>
                <div className="absolute bottom-120 z-[2] h-100 w-full md:bottom-48">
                    <FeaturedCard.GradationLayer />
                </div>
                {content.cardType === 'image' && (
                    <div className="absolute -top-4 z-[3] flex h-252 w-full overflow-hidden rounded-b-12 md:-top-8 md:h-256">
                        <FeaturedCard.CharacterImageLayer />
                    </div>
                )}
                <div
                    className={clsx(
                        'absolute top-0 z-[4] flex h-full w-full flex-col items-center px-20 pt-24 pb-15 md:flex-row md:py-36',
                        { 'md:px-32': content.cardType === 'default' },
                        { 'md:pl-32 md:pr-240': content.cardType === 'image' }
                    )}
                >
                    <FeaturedCard.ContentLayer />
                </div>
            </FeaturedCard>
        </HomeFeaturedCardContext.Provider>
    );
}

type ParsedPlacementParams = {
    cardType: 'default' | 'image';
    cardColor?: string;
    cardImageUrl?: string;
    coverImageUrl?: string;
    coverBlurHash?: string;
    characterImageUrl?: string;
    title?: string;
    status?: NovelItem_Status;
    slug?: string;
    rating?: number;
    synopsis?: string;
    isWtu?: boolean;
    series?: SeriesTypeItem;
};

HomeFeaturedCard.parsePlacementParams = (placement: PlacementEntryItem): ParsedPlacementParams | undefined => {
    const { series, innerEntry } = placement;
    if (isOneOfKind(innerEntry, 'card') && isOneOfKind(series?.item, 'novel')) {
        const { banner, characterImageUrl } = innerEntry.card || {};
        const { novel } = series?.item || {};
        const { activePricingModelType } = parseNovelStatus(novel);
        return {
            series: placement.series,
            cardType: characterImageUrl?.value ? 'image' : 'default',
            cardColor: banner?.colorCode,
            cardImageUrl: banner?.imageUrl,
            coverImageUrl: novel?.coverUrl?.value,
            coverBlurHash: novel?.coverBlurHash?.value,
            characterImageUrl: characterImageUrl?.value,
            title: novel?.name,
            status: novel?.status,
            slug: novel?.slug,
            rating: novel?.reviewInfo?.rating?.value,
            synopsis: novel?.synopsis?.value,
            isWtu: activePricingModelType === 'waitToUnlock',
        };
    }
};

export const HomeFeaturedCardContext = createContext<ParsedPlacementParams>({} as ParsedPlacementParams);

type FeaturedCardProps = {
    children: ReactNode;
};

function FeaturedCard({ children }: FeaturedCardProps) {
    const { slug, series } = useContext(HomeFeaturedCardContext);
    const { ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollectionItem,
        triggerOnce: true,
    });

    const handleClickSeries = () => {
        if (!isOneOfKind(series?.item, 'novel')) return;

        const novel = series.item.novel;

        logSelectSeries(novel);
    };

    return (
        <article
            className="relative mt-4 h-[320px] w-full rounded-12 shadow-ww-text-container md:mt-8 md:h-[248px]"
            ref={ref}
        >
            <Link
                onClick={handleClickSeries}
                to={getNovelPath(slug ?? '')}
                data-posthog-click-event={HomeEvents.ClickCollectionItem}
            >
                {children}
            </Link>
        </article>
    );
}

FeaturedCard.ContentLayer = ContentLayer;
FeaturedCard.GradationLayer = GradationLayer;
FeaturedCard.CharacterImageLayer = CharacterImageLayer;
FeaturedCard.BackgroundLayer = BackgroundLayer;
